import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import useAuth from '../../../../auth/useAuth';

import EditNameModal from './editNameModal';
import ResetPasswordModal from './resetPasswordModal';

import { 
  updateUserAlias,
  resetPassword
} from './api';

export default function UserProfile() {
  const { getUser, saveUser } = useAuth();

  const user = getUser();
  // Fallback labels are chained: alias_name → first_name.L → first_name → last_name → 'User'. Post token-only routing, first_name/last_name may be undefined until the app's next generalInfo fetch enriches the stored user.
  const initial = user?.last_name?.charAt(0) || '';
  const username = user?.alias_name
    || (user?.first_name && initial ? `${user.first_name}.${initial}` : user?.first_name || user?.last_name || 'User');

  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditNameModalShown, setIsEditNameModalShown] = useState(false);
  const [name, setName] = useState(user?.alias_name || '');
  const [isResetPasswordModalShown, setIsResetPasswordModalShown] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [warning, setWarning] = useState(null);

  const updateUserAliasMutation = useMutation({
    mutationFn: (data) => updateUserAlias(data),
    onSuccess() {
      const updatedUser = { ...user, alias_name: name };
      saveUser(updatedUser);
      setIsLoading(false);
      setIsEditNameModalShown(false);
      setWarning(null);
    },
    onError(error) {
      setWarning(error.response.data);
      setIsLoading(false);
    }
  });

  const updatePasswordMutation = useMutation({
    mutationFn: (data) => resetPassword(data),
    onSuccess() {
      setIsLoading(false);
      setIsResetPasswordModalShown(false);
      setOldPassword('');
      setNewPassword('');
      setWarning(null);      
    },
    onError(error) {
      setWarning(error.response.data);
      setIsLoading(false);
    }
  });

  const handleUsernameClick = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  }
  
  useEffect(() => {
    const handleClickOutside = () => {
      setIsDropdownOpen(false);
    };
    
    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);
  
  const handleDropdownClick = (e) => {
    e.stopPropagation();
  };
  
  const handleEditNameClick = () => {
    setIsEditNameModalShown(true);
  };
  
  const handleEditNameCloseButtonClick = () => {
    setWarning(null);
    setName(user?.alias_name || '');
    setIsEditNameModalShown(false);
  };

  const handleNameChange = (e) => {
    setName(e.target.value.trim());
  }
  
  const handleEditPasswordClick = () => {
    setIsResetPasswordModalShown(true);
  };
  
  const handleResetPasswordCloseButtonClick = () => {
    setWarning(null);
    setOldPassword('');
    setNewPassword('');
    setIsResetPasswordModalShown(false);
  };

  const handleOldPasswordChange = (e) => {
    setOldPassword(e.target.value.trim());
  }

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value.trim());
  }

  const handleEditNameSaveButtonClick = () => {
    setIsLoading(true);
    updateUserAliasMutation.mutate({
      id: user?.id,
      alias: name,
      email: user?.email,
      username: user?.username
    });
  }

  const handleResetPasswordSaveButtonClick = () => {
    setIsLoading(true);
    updatePasswordMutation.mutate({
      username: user?.username,
      oldPassword,
      newPassword
    });
  }
  
  return (
    <div className="header-profile">
      <button className="header-username" onClick={handleUsernameClick}>{username}</button>
      { isDropdownOpen &&
        <ul className="header-profile-dropdown" onClick={handleDropdownClick}>
          <li><button onClick={handleEditNameClick}>Edit Name</button></li>
          <li><button onClick={handleEditPasswordClick}>Reset Password</button></li>
        </ul>
      }
      {
        isEditNameModalShown &&
          <EditNameModal 
            name={name}
            onNameChange={handleNameChange}
            onSaveButtonClick={handleEditNameSaveButtonClick}
            onCloseButtonClick={handleEditNameCloseButtonClick}
            isLoading={isLoading}
            warning={warning}
          />
      }
      {
        isResetPasswordModalShown &&
          <ResetPasswordModal 
            oldPassword={oldPassword}
            onOldPasswordChange={handleOldPasswordChange}
            newPassword={newPassword}
            onNewPasswordChange={handleNewPasswordChange}
            onSaveButtonClick={handleResetPasswordSaveButtonClick}
            onCloseButtonClick={handleResetPasswordCloseButtonClick}
            isLoading={isLoading}
            warning={warning}
          />
      }
    </div>
  );
};
